import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FiSearch, FiX, FiPlus, FiEdit2 } from 'react-icons/fi';
import CreateShopForm from './CreateShopForm.jsx';
import EditShopModal from './EditShopModal.jsx';
/** Remove spaces; if exactly 8 digits (no leading +), prefix with +65; else return trimmed. */
const normalizePhone = (value) => {
    if (value == null) return '';
    const cleaned = String(value).replace(/\s/g, '').trim();
    if (!cleaned) return '';
    // Already has + → no change (user said: +123456789 then no change)
    if (cleaned.startsWith('+')) return cleaned;
    // Exactly 8 digits → +65 + digits
    if (/^\d{8}$/.test(cleaned)) return '+65' + cleaned;
    return cleaned;
};

const SERVICE_OPTIONS = ['Massage', 'Facial', 'IPL/Waxing', 'FJ', 'BJ', 'JuaGen', 'Bubble Bath'];

const CreateGodMemberForm = ({ onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        godID: '',
        godName: '',
        nationality: '',
        age: '',
        webID: '',
        memberType: 'Member',
        godTGs: [{ godTGID: '', godTGNumber: '', godTGUsername: '', notInUse: false }],
        shopId: '',
        hideForShop: false,
        autobot: false,
        isMonitor: false,
        noWebsite: false,
        contactNumbers: [{ contactNumber: '', isActive: true }],
        whatsAppNumbers: [{ whatsAppNumber: '', isActive: true }],
        status: 'active',
        openHour: '10:30',
        closeHour: '22:30',
        remarks: '',
        renewalDate: '',
        referredBy: null,
        wechatQR: [],
        standardPhotos: [],
        icecreamPhotos: [],
        standardVideos: [],
        icecreamVideos: [],
        photos: false,
        updateContact: false,
        priceHr: '',
        service: ['Massage'],
        extraService: '',
        websiteRemarks: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [memberSearchTerm, setMemberSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showMemberSearch, setShowMemberSearch] = useState(false);
    const [memberSearchLoading, setMemberSearchLoading] = useState(false);
    const [memberSearchReturnedEmpty, setMemberSearchReturnedEmpty] = useState(false);
    const [selectedReferrer, setSelectedReferrer] = useState(null);
    const [shops, setShops] = useState([]);
    const [shopFilter, setShopFilter] = useState('');
    const [showShopDropdown, setShowShopDropdown] = useState(false);
    const [lastCreatedGodID, setLastCreatedGodID] = useState('');
    const [lastCreatedWebID, setLastCreatedWebID] = useState('');
    const [showCreateShopForm, setShowCreateShopForm] = useState(false);
    const [showEditShopModal, setShowEditShopModal] = useState(false);

    const selectedShop = formData.shopId && shops.length ? shops.find((s) => s._id === formData.shopId) : null;

    const priceHrFieldLabel = useMemo(() => {
        if (!selectedShop) return 'Price / hr (optional)';
        const mp = selectedShop.massagePrice1hr;
        if (mp == null || mp === '') return 'Price / hr (optional)';
        const n = Number(mp);
        if (!Number.isFinite(n)) return 'Price / hr (optional)';
        const display = Number.isInteger(n) ? String(n) : n.toFixed(2);
        return `Price / hr ($${display})`;
    }, [selectedShop]);

    const refetchShops = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/shops?limit=500`,
                { headers: { 'Authorization': `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' } }
            );
            if (res.data.success) setShops(res.data.data || []);
        } catch (err) {
            console.error('Error refetching shops:', err);
        }
    };

    const filteredShops = useMemo(() => {
        if (!shopFilter.trim()) return shops;
        const q = shopFilter.trim().toLowerCase();
        const filtered = shops.filter(
            (s) =>
                (s.shopName && s.shopName.toLowerCase().includes(q)) ||
                (s.address && s.address.toLowerCase().includes(q)) ||
                (s.region && s.region.toLowerCase().includes(q)) ||
                (s.area && s.area.toLowerCase().includes(q))
        );
        // Keep selected shop in list if it was filtered out
        if (formData.shopId && !filtered.some((s) => s._id === formData.shopId)) {
            const selected = shops.find((s) => s._id === formData.shopId);
            if (selected) return [selected, ...filtered];
        }
        return filtered;
    }, [shops, shopFilter, formData.shopId]);

    useEffect(() => {
        const fetchShops = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/shops?limit=500`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                if (res.data.success) setShops(res.data.data || []);
            } catch (err) {
                console.error('Error fetching shops:', err);
            }
        };
        fetchShops();
    }, []);

    useEffect(() => {
        const fetchLastCreatedMember = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/godmembers?page=1&limit=1&sort=-createdAt`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                if (res.data.success && res.data.data?.length > 0) {
                    const last = res.data.data[0];
                    setLastCreatedGodID(last.godID != null ? String(last.godID) : '');
                    setLastCreatedWebID(last.webID != null ? String(last.webID) : '');
                }
            } catch (err) {
                console.error('Error fetching last created member:', err);
            }
        };
        fetchLastCreatedMember();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'status' && value === 'cancelled') {
            setFormData(prev => ({ ...prev, status: value, isMonitor: false }));
            return;
        }
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const toggleServiceOption = (option) => {
        setFormData((prev) => {
            const cur = new Set(prev.service || []);
            if (cur.has(option)) cur.delete(option);
            else cur.add(option);
            let next = [...cur];
            if (next.length === 0) next = ['Massage'];
            return { ...prev, service: next };
        });
    };

    const handleContactNumberChange = (index, value) => {
        const newContactNumbers = [...formData.contactNumbers];
        newContactNumbers[index] = { ...newContactNumbers[index], contactNumber: value };
        setFormData(prev => ({ ...prev, contactNumbers: newContactNumbers }));
    };

    const handleContactNumberBlur = (index) => {
        const v = formData.contactNumbers[index]?.contactNumber;
        const normalized = normalizePhone(v);
        if (normalized !== v) {
            const newContactNumbers = [...formData.contactNumbers];
            newContactNumbers[index] = { ...newContactNumbers[index], contactNumber: normalized };
            setFormData(prev => ({ ...prev, contactNumbers: newContactNumbers }));
        }
    };

    const addContactNumber = () => {
        setFormData(prev => ({
            ...prev,
            contactNumbers: [...prev.contactNumbers, { contactNumber: '', isActive: true }]
        }));
    };

    const removeContactNumber = (index) => {
        setFormData(prev => ({
            ...prev,
            contactNumbers: prev.contactNumbers.filter((_, i) => i !== index)
        }));
    };

    const handleWhatsAppNumberChange = (index, value) => {
        const newWhatsAppNumbers = [...formData.whatsAppNumbers];
        newWhatsAppNumbers[index] = { ...newWhatsAppNumbers[index], whatsAppNumber: value };
        setFormData(prev => ({ ...prev, whatsAppNumbers: newWhatsAppNumbers }));
    };

    const handleWhatsAppNumberBlur = (index) => {
        const v = formData.whatsAppNumbers[index]?.whatsAppNumber;
        const normalized = normalizePhone(v);
        if (normalized !== v) {
            const newWhatsAppNumbers = [...formData.whatsAppNumbers];
            newWhatsAppNumbers[index] = { ...newWhatsAppNumbers[index], whatsAppNumber: normalized };
            setFormData(prev => ({ ...prev, whatsAppNumbers: newWhatsAppNumbers }));
        }
    };

    const addWhatsAppNumber = () => {
        setFormData(prev => ({
            ...prev,
            whatsAppNumbers: [...prev.whatsAppNumbers, { whatsAppNumber: '', isActive: true }]
        }));
    };

    const removeWhatsAppNumber = (index) => {
        setFormData(prev => ({
            ...prev,
            whatsAppNumbers: prev.whatsAppNumbers.filter((_, i) => i !== index)
        }));
    };

    const addTelegram = () => {
        setFormData(prev => ({
            ...prev,
            godTGs: [...prev.godTGs, { godTGID: '', godTGNumber: '', godTGUsername: '', notInUse: false }]
        }));
    };

    const removeTelegram = (index) => {
        setFormData(prev => ({
            ...prev,
            godTGs: prev.godTGs.filter((_, i) => i !== index)
        }));
    };

    const handleGodTGChange = (index, field, value) => {
        if (field === 'godTGUsername' && typeof value === 'string' && value.startsWith('@')) {
            value = value.slice(1);
        }
        const newGodTGs = [...formData.godTGs];
        newGodTGs[index] = { ...newGodTGs[index], [field]: value };
        setFormData(prev => ({ ...prev, godTGs: newGodTGs }));
    };

    const handleGodTGCheckbox = (index, field, checked) => {
        const newGodTGs = [...formData.godTGs];
        newGodTGs[index] = { ...newGodTGs[index], [field]: checked };
        setFormData(prev => ({ ...prev, godTGs: newGodTGs }));
    };

    const handleGodTGNumberBlur = (index) => {
        const v = formData.godTGs[index]?.godTGNumber;
        const normalized = normalizePhone(v);
        if (normalized !== v) {
            const newGodTGs = [...formData.godTGs];
            newGodTGs[index] = { ...newGodTGs[index], godTGNumber: normalized };
            setFormData(prev => ({ ...prev, godTGs: newGodTGs }));
        }
    };

    const handleMemberSearch = async (searchTerm) => {
        if (!searchTerm.trim()) {
            setSearchResults([]);
            return;
        }
        setShowMemberSearch(true);
        setMemberSearchLoading(true);
        setMemberSearchReturnedEmpty(false);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/godmembers?search=${encodeURIComponent(searchTerm.trim())}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                const data = response.data.data || [];
                setSearchResults(data);
                setMemberSearchReturnedEmpty(data.length === 0);
            } else {
                setSearchResults([]);
                setMemberSearchReturnedEmpty(true);
            }
        } catch (err) {
            console.error('Error searching members:', err);
            setSearchResults([]);
            setMemberSearchReturnedEmpty(true);
        } finally {
            setMemberSearchLoading(false);
        }
    };

    const selectReferrer = (member) => {
        setSelectedReferrer(member);
        setFormData(prev => ({ ...prev, referredBy: member._id }));
        setShowMemberSearch(false);
        setMemberSearchTerm('');
        setSearchResults([]);
        setMemberSearchReturnedEmpty(false);
    };

    const clearReferrer = () => {
        setSelectedReferrer(null);
        setFormData(prev => ({ ...prev, referredBy: null }));
    };

    const selectShop = (shop) => {
        setFormData(prev => ({ ...prev, shopId: shop._id }));
        setShopFilter('');
        setShowShopDropdown(false);
    };

    const clearShop = () => {
        setFormData(prev => ({ ...prev, shopId: '' }));
        setShopFilter('');
    };

    const handleCreateShopSuccess = (newShop) => {
        setShowCreateShopForm(false);
        refetchShops().then(() => {
            if (newShop && newShop._id) {
                setFormData(prev => ({ ...prev, shopId: newShop._id }));
            }
        });
    };

    const handleEditShopSuccess = () => {
        setShowEditShopModal(false);
        refetchShops();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const godTGsCleaned = formData.godTGs
                .map((tg) => ({
                    godTGID: (tg.godTGID && tg.godTGID.trim()) || undefined,
                    godTGNumber: tg.godTGNumber ? normalizePhone(tg.godTGNumber) : undefined,
                    godTGUsername: (tg.godTGUsername && tg.godTGUsername.trim()) ? tg.godTGUsername.trim() : undefined,
                    notInUse: !!tg.notInUse
                }))
                .filter((tg) => tg.godTGID || tg.godTGNumber);
            const rawService = (formData.service || []).filter((s) => SERVICE_OPTIONS.includes(s));
            const servicePayload = rawService.length ? rawService : ['Massage'];
            const priceHrParsed =
                formData.priceHr === '' || formData.priceHr == null
                    ? undefined
                    : Number(formData.priceHr);
            const cleanedFormData = {
                ...formData,
                contactNumbers: formData.contactNumbers
                    .filter((entry) => (entry.contactNumber && entry.contactNumber.trim() !== ''))
                    .map((entry) => ({
                        contactNumber: normalizePhone(entry.contactNumber) || entry.contactNumber.trim(),
                        isActive: entry.isActive !== false
                    })),
                whatsAppNumbers: formData.whatsAppNumbers
                    .filter((entry) => (entry.whatsAppNumber && entry.whatsAppNumber.trim() !== ''))
                    .map((entry) => ({
                        whatsAppNumber: normalizePhone(entry.whatsAppNumber) || entry.whatsAppNumber.trim(),
                        isActive: entry.isActive !== false
                    })),
                age: formData.age ? new Date(formData.age).toISOString() : undefined,
                godTGs: godTGsCleaned,
                shopId: formData.shopId || undefined,
                hideForShop: !!formData.hideForShop,
                autobot: !!formData.autobot,
                isMonitor: formData.status === 'cancelled' ? false : !!formData.isMonitor,
                noWebsite: !!formData.noWebsite,
                priceHr: Number.isFinite(priceHrParsed) && priceHrParsed >= 0 ? priceHrParsed : undefined,
                service: servicePayload,
                extraService: (formData.extraService || '').trim() || undefined,
                websiteRemarks: (formData.websiteRemarks || '').trim() || undefined
            };
            delete cleanedFormData.godTGID;
            delete cleanedFormData.godTGNumber;

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/godmembers`,
                cleanedFormData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );
            if (response.data.success) {
                onSuccess && onSuccess(response.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to create God Member');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* Title row: title left, checkboxes in middle, x right */}
            <div className="flex justify-between items-center mb-6 gap-4">
                <h2 className="text-xl font-bold text-gray-900 shrink-0">Create New God Member</h2>
                <div className="flex-1 flex justify-center items-center gap-4">
                    <label className="inline-flex items-center">
                        <input
                            type="checkbox"
                            name="photos"
                            checked={formData.photos}
                            onChange={handleChange}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Photos</span>
                    </label>
                    <label className="inline-flex items-center">
                        <input
                            type="checkbox"
                            name="updateContact"
                            checked={formData.updateContact}
                            onChange={handleChange}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Update Contact</span>
                    </label>
                    <label className="inline-flex items-center">
                        <input
                            type="checkbox"
                            name="autobot"
                            checked={!!formData.autobot}
                            onChange={handleChange}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Autobot</span>
                    </label>
                    <label className="inline-flex items-center">
                        <input
                            type="checkbox"
                            name="isMonitor"
                            checked={!!formData.isMonitor}
                            onChange={handleChange}
                            disabled={formData.status === 'cancelled'}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Monitor</span>
                    </label>
                </div>
                <button
                    type="button"
                    onClick={onCancel}
                    className="text-gray-500 hover:text-gray-700 text-2xl leading-none p-1 shrink-0"
                    aria-label="Close"
                >
                    ×
                </button>
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Row 1: God ID, God Name, Web ID, Nationality, Referred By — fr units so gap doesn't overflow */}
                <div className="grid gap-4 grid-cols-1 md:grid-cols-[1fr_2.5fr_1fr_1.5fr_4fr]">
                    <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700">GodID (L:{lastCreatedGodID || '-'})</label>
                        <input
                            type="text"
                            name="godID"
                            value={formData.godID}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2"
                        />
                    </div>
                    <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700">God Name</label>
                        <input
                            type="text"
                            name="godName"
                            value={formData.godName}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2"
                        />
                    </div>
                    <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700">Web ID (L:{lastCreatedWebID || '-'})</label>
                        <input
                            type="text"
                            name="webID"
                            value={formData.webID}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2"
                        />
                    </div>
                    <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700">Nationality</label>
                        <input
                            type="text"
                            name="nationality"
                            value={formData.nationality}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2"
                        />
                    </div>
                    <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Referred By</label>
                        {selectedReferrer ? (
                            <div className="flex items-center gap-2 py-2 px-2 border rounded-md mt-1 bg-white min-h-[42px]">
                                <span className="text-sm truncate">{selectedReferrer.fullName}</span>
                                <button type="button" onClick={clearReferrer} className="text-gray-400 hover:text-gray-600 shrink-0">
                                    <FiX />
                                </button>
                            </div>
                        ) : (
                            <div className="relative mt-1 min-w-0 flex gap-1">
                                <input
                                    type="text"
                                    value={memberSearchTerm}
                                    onChange={(e) => setMemberSearchTerm(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleMemberSearch(memberSearchTerm);
                                        }
                                    }}
                                    onFocus={() => setShowMemberSearch(true)}
                                    placeholder="GodID or name, then Enter or click Search"
                                    className="block w-full min-w-0 rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 pl-2 pr-2 box-border"
                                />
                                <button
                                    type="button"
                                    onClick={() => handleMemberSearch(memberSearchTerm)}
                                    className="p-2 rounded-md border border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-600 shrink-0"
                                    aria-label="Search member"
                                >
                                    <FiSearch className="w-4 h-4" />
                                </button>
                                {showMemberSearch && (memberSearchLoading || searchResults.length > 0 || memberSearchReturnedEmpty) && (
                                    <div className="absolute z-10 left-0 right-0 top-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                                        {memberSearchLoading ? (
                                            <div className="p-2 text-gray-500 text-sm">Loading...</div>
                                        ) : searchResults.length > 0 ? (
                                            searchResults.map((m) => (
                                                <div
                                                    key={m._id}
                                                    className="p-2 hover:bg-gray-100 cursor-pointer text-sm"
                                                    onClick={() => selectReferrer(m)}
                                                >
                                                    {m.fullName}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="p-2 text-gray-500 text-sm">No matches found</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Row 2: Age, Member Type, Status, Renewal Date */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Age</label>
                        <input
                            type="date"
                            name="age"
                            value={formData.age}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Member Type</label>
                        <select
                            name="memberType"
                            value={formData.memberType}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2"
                        >
                            <option value="Member">Member</option>
                            <option value="Admin">Admin</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2"
                        >
                            <option value="active">Active</option>
                            <option value="hold">Hold</option>
                            <option value="stop">Stop</option>
                            <option value="expiring">Expiring</option>
                            <option value="expired">Expired</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="changedName">Changed Name</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Renewal Date</label>
                        <input
                            type="date"
                            name="renewalDate"
                            value={formData.renewalDate}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2"
                        />
                    </div>
                </div>
                <div>
                    <label className="inline-flex items-center gap-2">
                        <input
                            type="checkbox"
                            name="noWebsite"
                            checked={!!formData.noWebsite}
                            onChange={handleChange}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">No website</span>
                    </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{priceHrFieldLabel}</label>
                        <input
                            type="number"
                            name="priceHr"
                            min="0"
                            step="0.01"
                            value={formData.priceHr}
                            onChange={handleChange}
                            placeholder="e.g. 88"
                            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Extra service (optional)</label>
                        <textarea
                            name="extraService"
                            value={formData.extraService}
                            onChange={handleChange}
                            rows={2}
                            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2 resize-y min-h-[44px]"
                            placeholder="Additional service note (line breaks allowed)"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Service</label>
                        <div className="flex flex-wrap gap-x-4 gap-y-2 p-3 rounded-md border border-gray-200 bg-gray-50/80">
                            {SERVICE_OPTIONS.map((opt) => (
                                <label key={opt} className="inline-flex items-center gap-2 cursor-pointer shrink-0">
                                    <input
                                        type="checkbox"
                                        checked={(formData.service || []).includes(opt)}
                                        onChange={() => toggleServiceOption(opt)}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-800">{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Website remarks (optional)</label>
                        <textarea
                            name="websiteRemarks"
                            value={formData.websiteRemarks}
                            onChange={handleChange}
                            rows={2}
                            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2 resize-y min-h-[44px]"
                            placeholder="Notes for website / listing (line breaks allowed)"
                        />
                    </div>
                </div>

                {/* Row 3: TG ID, TG Number — Add Telegram (multiple) */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">Telegram (TG ID / TG Number)</label>
                        <button
                            type="button"
                            onClick={addTelegram}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                            Add Telegram
                        </button>
                    </div>
                    <div className="space-y-2">
                        {formData.godTGs.map((tg, index) => (
                            <div key={index} className="flex gap-4 items-center flex-wrap">
                                <div className="flex-1 min-w-[140px]">
                                    <input
                                        type="text"
                                        value={tg.godTGID || ''}
                                        onChange={(e) => handleGodTGChange(index, 'godTGID', e.target.value)}
                                        placeholder="TG ID"
                                        className="block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2"
                                    />
                                </div>
                                <div className="flex-1 min-w-[140px]">
                                    <input
                                        type="text"
                                        value={tg.godTGNumber || ''}
                                        onChange={(e) => handleGodTGChange(index, 'godTGNumber', e.target.value)}
                                        onBlur={() => handleGodTGNumberBlur(index)}
                                        placeholder="TG Number (e.g. 88881234 → +65)"
                                        className="block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2"
                                    />
                                </div>
                                <div className="flex-1 min-w-[120px]">
                                    <input
                                        type="text"
                                        value={tg.godTGUsername || ''}
                                        onChange={(e) => handleGodTGChange(index, 'godTGUsername', e.target.value)}
                                        placeholder="Username without @"
                                        className="block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2"
                                    />
                                </div>
                                <label className="inline-flex items-center gap-2 shrink-0">
                                    <input
                                        type="checkbox"
                                        checked={!!tg.notInUse}
                                        onChange={(e) => handleGodTGCheckbox(index, 'notInUse', e.target.checked)}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">Not in use</span>
                                </label>
                                {formData.godTGs.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeTelegram(index)}
                                        className="px-3 py-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200 text-sm"
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Row 4: Contact Numbers, WhatsApp Numbers */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Contact Numbers</label>
                        {formData.contactNumbers.map((entry, index) => (
                            <div key={index} className="flex gap-2 mb-2">
                                <input
                                    type="text"
                                    value={entry.contactNumber || ''}
                                    onChange={(e) => handleContactNumberChange(index, e.target.value)}
                                    onBlur={() => handleContactNumberBlur(index)}
                                    placeholder="e.g. 12345555 → +65"
                                    className="flex-1 rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2"
                                />
                                {index > 0 && (
                                    <button type="button" onClick={() => removeContactNumber(index)} className="px-3 py-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200 text-sm">
                                        Remove
                                    </button>
                                )}
                            </div>
                        ))}
                        <button type="button" onClick={addContactNumber} className="mt-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 text-sm">
                            Add Contact Number
                        </button>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Numbers</label>
                        {formData.whatsAppNumbers.map((entry, index) => (
                            <div key={index} className="flex gap-2 mb-2">
                                <input
                                    type="text"
                                    value={entry.whatsAppNumber || ''}
                                    onChange={(e) => handleWhatsAppNumberChange(index, e.target.value)}
                                    onBlur={() => handleWhatsAppNumberBlur(index)}
                                    placeholder="e.g. 88881234 → +65"
                                    className="flex-1 rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2"
                                />
                                {index > 0 && (
                                    <button type="button" onClick={() => removeWhatsAppNumber(index)} className="px-3 py-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200 text-sm">
                                        Remove
                                    </button>
                                )}
                            </div>
                        ))}
                        <button type="button" onClick={addWhatsAppNumber} className="mt-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 text-sm">
                            Add WhatsApp Number
                        </button>
                    </div>
                </div>

                {/* Row 5: Shop — search by shop name/address, select one; + New Shop; Edit (pencil); Hide for shop checkbox same row */}
                <div className="flex flex-wrap items-end gap-4">
                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Shop</label>
                        {selectedShop ? (
                            <div className="flex items-center gap-2 py-2 px-2 border rounded-md bg-white min-h-[42px]">
                                <span className="text-sm truncate flex-1 min-w-0">
                                    {selectedShop.shopName}
                                    {(selectedShop.region || selectedShop.area) && ` — ${[selectedShop.region, selectedShop.area].filter(Boolean).join(', ')}`}
                                    {selectedShop.address && ` · ${selectedShop.address}`}
                                </span>
                                <button type="button" onClick={() => setShowEditShopModal(true)} className="text-gray-500 hover:text-blue-600 shrink-0 p-1" aria-label="Edit shop">
                                    <FiEdit2 className="w-4 h-4" />
                                </button>
                                <button type="button" onClick={clearShop} className="text-gray-400 hover:text-gray-600 shrink-0">
                                    <FiX />
                                </button>
                            </div>
                        ) : (
                            <div className="flex gap-2">
                                <div className="relative flex-1 min-w-0">
                                    <input
                                        type="text"
                                        value={shopFilter}
                                        onChange={(e) => setShopFilter(e.target.value)}
                                        onFocus={() => setShowShopDropdown(true)}
                                        placeholder="Search by shop name or address..."
                                        className="block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2"
                                    />
                                    <FiSearch className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    {showShopDropdown && filteredShops.length > 0 && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                                            {filteredShops.map((shop) => (
                                                <div
                                                    key={shop._id}
                                                    className="p-2 hover:bg-gray-100 cursor-pointer text-sm"
                                                    onClick={() => selectShop(shop)}
                                                >
                                                    {shop.shopName}
                                                    {(shop.region || shop.area) && ` — ${[shop.region, shop.area].filter(Boolean).join(', ')}`}
                                                    {shop.address && ` · ${shop.address}`}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowCreateShopForm(true)}
                                    className="px-3 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 shrink-0 flex items-center gap-1 text-sm font-medium"
                                >
                                    <FiPlus className="w-4 h-4" /> New Shop
                                </button>
                            </div>
                        )}
                    </div>
                    <label className="inline-flex items-center gap-2 shrink-0 pb-1">
                        <input
                            type="checkbox"
                            checked={!!formData.hideForShop}
                            onChange={(e) => setFormData(prev => ({ ...prev, hideForShop: e.target.checked }))}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Hide for shop</span>
                    </label>
                </div>

                {/* Opening / Closing hours (optional) */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Opening hour</label>
                        <input
                            type="time"
                            name="openHour"
                            value={formData.openHour}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Closing hour</label>
                        <input
                            type="time"
                            name="closeHour"
                            value={formData.closeHour}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                    </div>
                </div>

                {/* Row 6: Remarks */}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Remarks</label>
                    <textarea
                        name="remarks"
                        value={formData.remarks}
                        onChange={handleChange}
                        rows="4"
                        className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2"
                    />
                </div>

                <div className="flex justify-end space-x-4">
                    <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200">
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {loading ? 'Creating...' : 'Create God Member'}
                    </button>
                </div>
            </form>

            {showCreateShopForm && (
                <CreateShopForm
                    onSuccess={handleCreateShopSuccess}
                    onCancel={() => setShowCreateShopForm(false)}
                />
            )}
            {showEditShopModal && selectedShop && (
                <EditShopModal
                    shop={selectedShop}
                    onSuccess={handleEditShopSuccess}
                    onCancel={() => setShowEditShopModal(false)}
                />
            )}
        </>
    );
};

export default CreateGodMemberForm;
